import * as Api from '@ViewModels';

export const DEFAULT_FILE_REQUEST_TITLE = 'Files Requested';

const NEW_REQUEST_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

export enum FileRequestFilter {
	Open = 'open',
	Waiting = 'waiting',
}

export enum FileRequestState {
	Waiting = 'waiting',
	Uploaded = 'uploaded',
}

export interface IContactFileRequest {
	responseId: string;
	surveyId: string;
	title: string;
	description?: string;
	requestedDate?: string;
	expirationDate?: string;
	senderUserId?: string;
	uploadedFileCount?: number;
}

export interface IFileRequestGroup {
	request: IContactFileRequest;
	files: Api.ISharedFile[];
	state: FileRequestState;
	isNew: boolean;
}

export function toContactFileRequest(
	response: Api.IFileRequestSurveyResponse,
	survey: Partial<Api.IFileRequestSurvey> | undefined
): IContactFileRequest {
	return {
		responseId: response.id,
		surveyId: response.surveyId,
		title: survey?.name?.trim() || DEFAULT_FILE_REQUEST_TITLE,
		description: survey?.description,
		requestedDate: response.creationDate,
		expirationDate: survey?.expirationDate,
		senderUserId: response.emailSenderUserId,
		uploadedFileCount: response.files?.length ?? 0,
	};
}

export function getFileRequestResponseId(file: Api.ISharedFile): string | undefined {
	return (
		file.origin?.secureLinkMetadata?.fileRequestResponseId ??
		file.history?.find(transaction => transaction.secureLinkMetadata?.fileRequestResponseId)?.secureLinkMetadata
			?.fileRequestResponseId
	);
}

function isRecent(date: string | undefined, now: Date) {
	return !!date && now.getTime() - new Date(date).getTime() <= NEW_REQUEST_WINDOW_MS;
}

function sortTier(group: IFileRequestGroup) {
	if (group.isNew) {
		return 0;
	}
	return group.state === FileRequestState.Waiting ? 1 : 2;
}

function expirationTime(group: IFileRequestGroup) {
	return group.request.expirationDate ? new Date(group.request.expirationDate).getTime() : Number.POSITIVE_INFINITY;
}

function compareGroups(a: IFileRequestGroup, b: IFileRequestGroup) {
	const aExpires = expirationTime(a);
	const bExpires = expirationTime(b);
	return sortTier(a) - sortTier(b) || (aExpires === bExpires ? 0 : aExpires - bExpires);
}

export function groupContactFiles(requests: IContactFileRequest[], files: Api.ISharedFile[], now: Date) {
	const filesByResponseId = new Map<string, Api.ISharedFile[]>(
		requests.map(request => [request.responseId, [] as Api.ISharedFile[]])
	);
	const sentFiles: Api.ISharedFile[] = [];
	const otherFiles: Api.ISharedFile[] = [];

	for (const file of files) {
		if (file.origin?.direction === Api.SharedFileDirection.Sent) {
			sentFiles.push(file);
			continue;
		}
		const requestFiles = filesByResponseId.get(getFileRequestResponseId(file) ?? '');
		if (requestFiles) {
			requestFiles.push(file);
		} else {
			otherFiles.push(file);
		}
	}

	const groups = requests
		.map<IFileRequestGroup>(request => {
			const requestFiles = filesByResponseId.get(request.responseId) ?? [];
			const hasUploads = requestFiles.length > 0 || (request.uploadedFileCount ?? 0) > 0;
			return {
				request,
				files: requestFiles,
				state: hasUploads ? FileRequestState.Uploaded : FileRequestState.Waiting,
				isNew: isRecent(request.requestedDate, now),
			};
		})
		.sort(compareGroups);

	return { groups, sentFiles, otherFiles };
}

export function filterFileRequestGroups(groups: IFileRequestGroup[], filter: FileRequestFilter) {
	if (filter === FileRequestFilter.Waiting) {
		return groups.filter(group => group.state === FileRequestState.Waiting);
	}
	return groups;
}
